import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { MessageKey } from '@/i18n'
import { Btn, Cta, Footer, ItemRow, Row, Strip, V1Screen, WaitCta } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { cropName, cropWord, kg, shortCode, shortLot, tr } from '@/features/gate/v1/text'
import {
  availability,
  bagsAt,
  declarationInfo,
  placePath,
  type BagView,
  type StoreState,
} from '../projection'
import { classifyScan } from '../rules'
import { useRefusalText } from '../components/useRefusalText'
import { useNow } from '../useStore'
import { Hint, holdNote, IconBox, StoreHeader, Tally, VehicleRow, WithStore } from './bits'
import { lotHoldLine } from './logic'
import { SIcon } from './icons'
import { TagScanner } from './TagScanner'

/** What the screen shows: the scanner (null), a place, a lot, or the vehicles on the road. */
type View =
  null | { kind: 'place'; id: string } | { kind: 'lot'; unitId: string | null } | { kind: 'trips' }

/**
 * V1 — क्या कहाँ है: scan a place label or a bag tag (or pick a place) and see what is there,
 * in big words. Same stock as the full stock screen (StockPage): worked out from the godown list
 * plus this phone's own records.
 */
export function V1Stock() {
  const navigate = useNavigate()
  const home = () => navigate('/m/store')
  return <WithStore onBack={home}>{(state) => <StockFlow state={state} onHome={home} />}</WithStore>
}

function StockFlow({ state, onHome }: { state: StoreState; onHome: () => void }) {
  const [view, setView] = useState<View>(null)
  const back = () => setView(null)
  if (view?.kind === 'place')
    return (
      <PlaceView
        key={view.id}
        state={state}
        id={view.id}
        onPlace={(id) => setView({ kind: 'place', id })}
        onLot={(unitId) => setView({ kind: 'lot', unitId })}
        onBack={back}
      />
    )
  if (view?.kind === 'lot')
    return (
      <LotView
        state={state}
        unitId={view.unitId}
        onPlace={(id) => setView({ kind: 'place', id })}
        onBack={back}
      />
    )
  if (view?.kind === 'trips') return <TripsView state={state} onBack={back} />
  return <Start state={state} onView={setView} onHome={onHome} />
}

// ---------------------------------------------------------------------------------------------
// Start: scan, or pick a place
// ---------------------------------------------------------------------------------------------

function Start({
  state,
  onView,
  onHome,
}: {
  state: StoreState
  onView: (v: View) => void
  onHome: () => void
}) {
  const refusal = useRefusalText()
  const payloads = useMemo(
    () => [
      ...state.locationByCode.keys(),
      ...state.bagByPayload.keys(),
      ...state.sampleByPayload.keys(),
    ],
    [state.locationByCode, state.bagByPayload, state.sampleByPayload],
  )
  const places = [...state.locations.values()].filter(
    (l) => l.parent_id === state.siteId && l.active,
  )
  const dock = bagsAt(state, state.siteId, false).length
  const onRoad = [...state.trips.values()].filter(
    (x) => x.status === 'IN_TRANSIT' || x.status === 'LOADING',
  ).length

  const onCode = (code: string): string | null => {
    const k = classifyScan(state, code)
    if (k.kind === 'DEAD') return refusal(k.status === 'VOID' ? 'TAG_VOID' : 'TAG_RETIRED')
    if (k.kind === 'UNKNOWN') return refusal('TAG_UNKNOWN')
    if (k.kind === 'LOCATION') onView({ kind: 'place', id: k.location.id })
    else if (k.kind === 'BAG') onView({ kind: 'lot', unitId: k.bag.unit_id })
    else onView({ kind: 'lot', unitId: k.sample.unit_id ?? null })
    return null
  }

  return (
    <V1Screen
      header={<StoreHeader onBack={onHome} icon="search" title={tr('store.v1.stock.title')} />}
      footer={
        <Footer>
          <Row>
            <Btn icon="truck" onClick={() => onView({ kind: 'trips' })}>
              {tr('store.v1.stock.onRoad', { count: onRoad })}
            </Btn>
          </Row>
          <WaitCta>{tr('store.v1.stock.scan')}</WaitCta>
        </Footer>
      }
    >
      <Hint icon="tag" text={tr('store.v1.stock.hint')} />
      <TagScanner hint={tr('store.v1.stock.scan')} payloads={payloads} onCode={onCode} />
      <b style={{ fontSize: 18, marginTop: 4 }}>{tr('store.v1.stock.orPick')}</b>
      {dock > 0 && (
        <Strip icon="sack" onClick={() => onView({ kind: 'place', id: state.siteId })}>
          {tr('store.v1.put.onDock', { count: dock })}
        </Strip>
      )}
      {places.map((p) => (
        <PlaceRow
          key={p.id}
          state={state}
          id={p.id}
          onClick={() => onView({ kind: 'place', id: p.id })}
        />
      ))}
    </V1Screen>
  )
}

/** A place to open: its name, its use when not ordinary storage, the bags in it. */
function PlaceRow({ state, id, onClick }: { state: StoreState; id: string; onClick: () => void }) {
  const loc = state.locations.get(id)
  if (!loc) return null
  const n = bagsAt(state, id).length
  return (
    <ItemRow
      onClick={onClick}
      padding="8px 10px"
      border={C.border}
      lead={
        <IconBox icon={loc.role === 'CONVENTIONAL' ? 'redtie' : 'godown'} size={44} iconSize={26} />
      }
      title={loc.name ?? loc.code}
      line={
        loc.role && loc.role !== 'STORAGE' ? tr(`store.role.${loc.role}` as MessageKey) : undefined
      }
      right={
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tally value={String(n)} label={tr('store.v1.stock.bagsWord', { count: n })} />
          <span style={{ color: C.acc, display: 'flex' }}>
            <SIcon name="next" size={24} sw={2.5} />
          </span>
        </span>
      }
    />
  )
}

// ---------------------------------------------------------------------------------------------
// A place: what is in it
// ---------------------------------------------------------------------------------------------

interface LotRow {
  unit_id: string
  bags: BagView[]
  gramsNow: number
}

/** Bags grouped by lot, with the grain in them now (else at the gate). */
function lotRows(bags: BagView[]): LotRow[] {
  const m = new Map<string, LotRow>()
  for (const b of bags) {
    const r = m.get(b.unit_id) ?? { unit_id: b.unit_id, bags: [], gramsNow: 0 }
    r.bags.push(b)
    r.gramsNow += b.net_g_current ?? b.net_g_gate ?? 0
    m.set(b.unit_id, r)
  }
  return [...m.values()]
}

function PlaceView({
  state,
  id,
  onPlace,
  onLot,
  onBack,
}: {
  state: StoreState
  id: string
  onPlace: (id: string) => void
  onLot: (unitId: string) => void
  onBack: () => void
}) {
  const navigate = useNavigate()
  const now = useNow()
  const here = state.locations.get(id)
  const children = [...state.locations.values()].filter((l) => l.parent_id === id && l.active)
  const direct = useMemo(() => bagsAt(state, id, false), [state, id])
  const all = useMemo(() => bagsAt(state, id), [state, id])
  if (!here) return null
  const available = all.filter((b) => availability(state, b, now) === 'AVAILABLE').length
  const held = all.filter((b) => availability(state, b, now) === 'ON_HOLD').length
  const decl =
    here.level === 'BAY' || here.level === 'POSITION' ? declarationInfo(state, id, now) : null
  const up = here.parent_id && here.level !== 'SITE' ? here.parent_id : null
  const atSite = here.level === 'SITE'

  return (
    <V1Screen
      header={
        <StoreHeader
          onBack={up ? () => onPlace(up) : onBack}
          icon="search"
          title={tr('store.v1.stock.title')}
        />
      }
      footer={
        <Footer>
          <Row>
            {up && (
              <Btn icon="back" onClick={() => onPlace(up)}>
                {tr('store.v1.stock.up')}
              </Btn>
            )}
            {!atSite && (
              <Btn
                icon="ticks"
                onClick={() => navigate(`/m/store/count?location=${encodeURIComponent(here.id)}`)}
              >
                {tr('store.v1.stock.count')}
              </Btn>
            )}
          </Row>
          <Cta label={tr('store.v1.stock.another')} icon="search" lead onClick={onBack} />
        </Footer>
      }
    >
      <ItemRow
        padding="10px 12px"
        titleSize={22}
        lead={<IconBox icon={here.role === 'CONVENTIONAL' ? 'redtie' : 'godown'} size={52} />}
        title={here.name ?? (atSite ? (state.cache.site_name ?? here.code) : here.code)}
        line={
          <span style={{ color: C.muted }}>
            {placePath(state, id)}
            {here.role && here.role !== 'STORAGE'
              ? ` · ${tr(`store.role.${here.role}` as MessageKey)}`
              : ''}
          </span>
        }
        right={
          <Tally
            value={String(all.length)}
            label={tr('store.v1.stock.bagsWord', { count: all.length })}
          />
        }
      />
      {!here.active && <Hint icon="x" tone="warn" text={tr('store.refusal.LOCATION_INACTIVE')} />}
      {all.length > 0 && (
        <BigFigures
          rows={[
            [tr('store.v1.stock.ready'), String(available), C.ok],
            ...(held > 0 ? ([[tr('store.v1.stock.held'), String(held), C.bad]] as const) : []),
          ]}
        />
      )}
      {decl && decl.status !== 'CLEAN' && here.role !== 'CONVENTIONAL' && (
        <Hint icon="bang" tone="warn" text={tr('store.v1.stock.noForm')} />
      )}
      {decl && decl.status === 'CLEAN' && decl.renewSoon && (
        <Hint
          icon="clock"
          tone="warn"
          text={tr('store.v1.stock.formEnds', { count: Math.max(0, decl.daysLeft ?? 0) })}
        />
      )}
      {children.map((c) => (
        <PlaceRow key={c.id} state={state} id={c.id} onClick={() => onPlace(c.id)} />
      ))}
      {direct.length > 0 && (
        <b style={{ fontSize: 18, marginTop: 4 }}>
          {tr(atSite ? 'store.v1.stock.onDockTitle' : 'store.v1.stock.lotsHere')}
        </b>
      )}
      {lotRows(direct).map((r) => (
        <LotLine key={r.unit_id} state={state} row={r} now={now} onClick={() => onLot(r.unit_id)} />
      ))}
      {all.length === 0 && children.length === 0 && (
        <Strip icon="sack">{tr('store.v1.stock.empty')}</Strip>
      )}
    </V1Screen>
  )
}

/** The good / held figures in big type. */
function BigFigures({ rows }: { rows: ReadonlyArray<readonly [string, string, string]> }) {
  return (
    <section
      style={{
        background: C.surface,
        border: `1.5px solid ${C.rule}`,
        borderRadius: 12,
        padding: '8px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {rows.map(([k, v, color]) => (
        <div
          key={k}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
            fontWeight: 700,
          }}
        >
          <span style={{ fontSize: 18 }}>{k}</span>
          <span style={{ fontSize: 28, color }}>{v}</span>
        </div>
      ))}
    </section>
  )
}

/** What a keeper must know about some bags: held, red tie still to tie, not sent yet. */
function flagsOf(state: StoreState, bags: readonly BagView[], now: number): string[] {
  const out: string[] = []
  if (bags.some((b) => availability(state, b, now) === 'ON_HOLD'))
    out.push(tr('store.v1.stock.flagHeld'))
  if (bags.some((b) => b.red_tie_pending)) out.push(tr('store.v1.stock.flagRedTie'))
  if (bags.some((b) => b.sync_error)) out.push(tr('store.v1.stock.flagProblem'))
  else if (bags.some((b) => b.pending_command_id)) out.push(tr('store.v1.stock.flagNotSent'))
  return out
}

function LotLine({
  state,
  row,
  now,
  onClick,
}: {
  state: StoreState
  row: LotRow
  now: number
  onClick: () => void
}) {
  const lot = state.lots.get(row.unit_id)
  const first = row.bags[0]
  const red = availability(state, first!, now) === 'ORDINARY'
  const days = first?.in_since
    ? Math.max(0, Math.floor((now - new Date(first.in_since).getTime()) / 86_400_000))
    : null
  // A lot the office stopped says why, in place of the bare "stopped by the office".
  const holdLine = lotHoldLine(lot)
  const flags = flagsOf(state, row.bags, now).filter(
    (f) => !holdLine || f !== tr('store.v1.stock.flagHeld'),
  )
  if (holdLine) flags.unshift(holdLine)
  return (
    <ItemRow
      onClick={onClick}
      padding="8px 10px"
      lead={
        <span style={{ color: red ? C.ink : C.ok, display: 'flex' }}>
          <SIcon name={red ? 'redtie' : 'sack'} size={30} />
        </span>
      }
      title={`${tr(red ? 'store.v1.band.red' : 'store.v1.band.good')} · ${lot ? shortLot(lot.code) : '—'}`}
      line={
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span>
            {tr('store.v1.stock.kg', { kg: kg(row.gramsNow) })}
            {days !== null ? ` · ${tr('store.v1.stock.days', { count: days })}` : ''}
          </span>
          {flags.length > 0 && (
            <span style={{ color: C.bad, fontWeight: 700 }}>{flags.join(' · ')}</span>
          )}
        </span>
      }
      right={
        <Tally
          value={`${row.bags.length}/${lot?.bag_count ?? row.bags.length}`}
          label={tr('store.v1.stock.bagsWord', { count: row.bags.length })}
        />
      }
    />
  )
}

// ---------------------------------------------------------------------------------------------
// A lot: where its bags are
// ---------------------------------------------------------------------------------------------

function LotView({
  state,
  unitId,
  onPlace,
  onBack,
}: {
  state: StoreState
  unitId: string | null
  onPlace: (id: string) => void
  onBack: () => void
}) {
  const now = useNow()
  const lot = unitId ? state.lots.get(unitId) : undefined
  const bags = unitId
    ? [...state.bags.values()].filter((b) => b.unit_id === unitId && b.status !== 'VOID')
    : []
  const byPlace = new Map<string, BagView[]>()
  for (const b of bags) {
    const key = b.shipment_id
      ? `trip:${b.shipment_id}`
      : b.status === 'MISSING'
        ? 'missing'
        : `loc:${b.location_id ?? ''}`
    byPlace.set(key, [...(byPlace.get(key) ?? []), b])
  }
  const footer = (
    <Footer>
      <Cta label={tr('store.v1.stock.another')} icon="search" lead onClick={onBack} />
    </Footer>
  )
  const header = <StoreHeader onBack={onBack} icon="search" title={tr('store.v1.stock.title')} />
  if (!lot)
    return (
      <V1Screen header={header} footer={footer}>
        <Hint icon="bang" tone="warn" text={tr('store.v1.stock.notHere')} />
      </V1Screen>
    )
  const red = lot.stream === 'CONV' || lot.unit_type === 'ORPHAN'
  // The list carries the crop's English name: the app's own word for it (gate.crop.<name>).
  const crop = lot.crop_name ? cropWord(lot.crop_name) : cropName([], lot.crop_id)
  const flags = flagsOf(state, bags, now)
  return (
    <V1Screen header={header} footer={footer}>
      <ItemRow
        padding="10px 12px"
        titleSize={22}
        lead={<IconBox icon={red ? 'redtie' : 'sack'} size={52} color={red ? C.ink : C.ok} />}
        title={tr('store.v1.stock.lot', { lot: shortLot(lot.code) })}
        line={
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{`${crop} · ${tr(red ? 'store.v1.band.red' : 'store.v1.band.good')}`}</span>
            {holdNote(lot)}
          </span>
        }
        right={
          <Tally
            value={`${bags.length}/${lot.bag_count}`}
            label={tr('store.v1.stock.bagsWord', { count: bags.length })}
          />
        }
      />
      {lot.hold !== 'NONE' && <Hint icon="lock" tone="warn" text={tr('store.v1.stock.holdLot')} />}
      {lot.hold === 'NONE' && flags.length > 0 && (
        <Hint icon="bang" tone="warn" text={flags.join(' · ')} />
      )}
      <b style={{ fontSize: 18, marginTop: 4 }}>{tr('store.v1.stock.where')}</b>
      {[...byPlace.entries()].map(([key, list]) => {
        const locId = key.startsWith('loc:') ? key.slice(4) : null
        const loc = locId ? state.locations.get(locId) : undefined
        const trip = key.startsWith('trip:') ? state.trips.get(key.slice(5)) : undefined
        const title =
          key === 'missing'
            ? tr('store.v1.stock.missing')
            : key.startsWith('trip:')
              ? tr('store.v1.stock.onTrip', {
                  vehicle: trip?.vehicle_no ?? trip?.code ?? key.slice(5),
                })
              : loc
                ? loc.level === 'SITE'
                  ? tr('store.v1.stock.dock')
                  : (loc.name ?? loc.code)
                : tr('store.v1.stock.elsewhere')
        return (
          <ItemRow
            key={key}
            onClick={loc ? () => onPlace(loc.id) : undefined}
            padding="8px 10px"
            border={key === 'missing' ? C.bad : loc ? C.border : C.rule}
            lead={
              <IconBox
                icon={key === 'missing' ? 'bang' : trip ? 'truck' : 'godown'}
                size={44}
                iconSize={26}
                color={key === 'missing' ? C.bad : C.acc}
              />
            }
            title={title}
            line={
              <span style={{ color: C.muted }}>
                {list.map((b) => shortCode(b.qr_payload)).join(', ')}
              </span>
            }
            right={
              <Tally
                value={String(list.length)}
                label={tr('store.v1.stock.bagsWord', { count: list.length })}
              />
            }
          />
        )
      })}
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// Vehicles on the road
// ---------------------------------------------------------------------------------------------

function TripsView({ state, onBack }: { state: StoreState; onBack: () => void }) {
  const trips = [...state.trips.values()].filter(
    (x) => x.status === 'IN_TRANSIT' || x.status === 'LOADING',
  )
  return (
    <V1Screen
      header={<StoreHeader onBack={onBack} icon="truck" title={tr('store.v1.stock.roadTitle')} />}
      footer={
        <Footer>
          <Cta label={tr('gate.v1.back')} icon="back" lead onClick={onBack} />
        </Footer>
      }
    >
      {trips.length === 0 && <Strip icon="truck">{tr('store.v1.stock.noRoad')}</Strip>}
      {trips.map((trip) => {
        const bags = trip.items.filter((i) => i.container_id).length
        const grams = trip.items.reduce(
          (s, i) => s + ((i.container_id && state.bags.get(i.container_id)?.net_g_gate) || 0),
          0,
        )
        const incoming = trip.to_location_id === state.siteId
        return (
          <VehicleRow
            key={trip.shipment_id}
            vehicle={trip.vehicle_no ?? trip.code}
            line={
              incoming
                ? tr('store.v1.stock.from', { place: trip.from_name ?? '—' })
                : tr('store.v1.stock.to', { place: trip.to_name ?? '—' })
            }
            extra={`${tr('store.v1.stock.bagsN', { count: bags })} · ${tr('store.v1.stock.kg', { kg: kg(grams) })}${
              trip.local ? ` · ${tr('store.v1.stock.flagNotSent')}` : ''
            }`}
          />
        )
      })}
    </V1Screen>
  )
}
